import { useUserStore } from '@/stores/user';
import { usePermissionStore } from '@/stores/permission';
import { useConfigStore } from '@/stores/config';

// 路由切换时同步 document.title 与 meta description（SEO 基础项）
function applySeo(to) {
    const seo = to.meta?.seo;
    const base = '优臣GEO';
    document.title = seo?.title || (to.meta?.title ? `${to.meta.title} - ${base}` : base);
    if (seo?.description) {
        let tag = document.querySelector('meta[name="description"]');
        if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute('name', 'description');
            document.head.appendChild(tag);
        }
        tag.setAttribute('content', seo.description);
    }
}

export function setupPermissionGuard(router) {
    router.beforeEach(async (to, from, next) => {
        const userStore = useUserStore();
        const permissionStore = usePermissionStore();
        const configStore = useConfigStore();

        // 公开配置（站点名/默认分页等）：首次导航前加载，之后 no-op
        await configStore.load();

        // 0. 公开页面（官网/登录/错误页）：直接放行，不触发权限加载——
        //    避免访客带着过期 token 访问官网时被强制踢到登录页
        if (to.meta.requiresAuth === false) {
            return next();
        }

        // 1. 检查是否已登录
        if (!userStore.token) {
            return next({ path: '/login', query: { redirect: to.fullPath } });
        }

        // 2. 已登录但未加载权限
        if (!permissionStore.loaded) {
            try {
                await permissionStore.loadPermissions();
            } catch (error) {
                userStore.logout();
                return next({ path: '/login' });
            }
        }

        // 3. 仅超级管理员可见的页面
        if (to.meta.superAdmin && userStore.userInfo?.userType !== 1) {
            return next({ path: '/403' });
        }

        // 3.1 仅指定角色类型可见的页面（如平台管理：仅超管与商户）
        if (to.meta.userTypes && !to.meta.userTypes.includes(userStore.userInfo?.userType)) {
            return next({ path: '/403' });
        }

        // 4. 检查页面权限（支持数组：任一权限满足即放行，如发布统计对代理商走 publish:stats）
        const requiredPerm = to.meta.permission;
        if (requiredPerm) {
            const hasPerm = Array.isArray(requiredPerm)
                ? permissionStore.hasAnyPermission(requiredPerm)
                : permissionStore.hasPermission(requiredPerm);
            if (!hasPerm) {
                return next({ path: '/403' });
            }
        }

        next();
    });

    router.afterEach((to) => applySeo(to));
}
